import React from "react";

import bildmarkeFooter from "@/assets/icons/Bildmarke_SUITES_Footer.svg";
import logoKappl from "@/assets/icons/Logo_Kappl_weiss.svg";
import logoIschgl from "@/assets/icons/Logo_Ischgl_weiss.svg";
import NewsletterDialog from "@/components/NewsletterDialogLazy";
import Reveal from "@/components/Reveal";
import { useLocale } from "@/contexts/LocaleContext";
import type { TranslationKey } from "@/lib/translations";
import { EditableT, EditableTLink, EditableImage } from "@/cms/EditableT";
import { Editable } from "@/cms/Editable";



const CREME = "#F2F2F2";
const ANTHRAZIT = "#383637";
const VIOLET = "#6C184F";

const PhoneIcon = () => (
  <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="shrink-0">
    <path fillRule="evenodd" clipRule="evenodd" d="M7.25135 1.80856C6.55396 0.367201 5.22777 -0.224365 1.59793 0.0761133C0.810546 0.222831 0.333616 0.803833 0.16489 1.81795C-0.189433 4.54572 0.0119127 7.36739 0.91853 9.96136C2.77226 15.2632 8.37282 21.1072 13.4537 23.0415C15.9396 23.9876 18.6426 24.1977 21.2578 23.8279C22.2297 23.6519 22.7865 23.1542 22.9271 22.3326C23.215 18.5449 22.6481 17.1611 21.2668 16.4334C20.5165 16.0378 17.4525 15.0601 16.8113 15.2937C14.8642 16.0038 14.601 20.6694 8.9881 14.6211C3.19295 8.76415 7.66417 8.48949 8.34357 6.45774C8.56741 5.78871 7.63042 2.59144 7.25135 1.80856Z" fill={VIOLET}/>
  </svg>
);

const MailIcon = () => (
  <svg width="29" height="20" viewBox="0 0 29 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="shrink-0">
    <path fillRule="evenodd" clipRule="evenodd" d="M2.06313 0H26.9369C27.5316 0 28.0697 0.255609 28.4463 0.663164C28.4336 0.671684 28.4209 0.681625 28.4095 0.691565L17.7852 9.57541L17.7696 9.58961L15.9075 11.146C15.5266 11.4655 15.0183 11.626 14.5085 11.6288C14.0001 11.6316 13.496 11.4769 13.1222 11.163L11.2389 9.58819L11.2233 9.57541L0.593311 0.685885C0.581982 0.675944 0.569238 0.667424 0.55791 0.658904C0.93457 0.252769 1.47124 0 2.06313 0ZM28.9561 1.64726L18.9675 10L28.9561 18.3527C28.9844 18.2164 29 18.0758 29 17.931V2.06901C29 1.92417 28.9844 1.78358 28.9561 1.64726ZM28.4463 19.3383C28.4336 19.3297 28.4209 19.3198 28.4095 19.3099L18.1222 10.7072L16.6 11.981C16.0194 12.4666 15.2632 12.7109 14.5127 12.7151C13.7608 12.7194 13.0061 12.4794 12.4298 11.998L10.8863 10.7072L0.593311 19.3141C0.581982 19.3241 0.569238 19.3326 0.55791 19.3411C0.93457 19.7458 1.47124 20 2.06455 20H26.9383C27.5316 20 28.0697 19.7444 28.4463 19.3383ZM0.0453125 18.3584L10.041 10L0.0453125 1.64158C0.0155762 1.77932 0 1.92275 0 2.06901V17.9324C0 18.0773 0.0155762 18.2207 0.0453125 18.3584Z" fill={VIOLET}/>
  </svg>
);

const navLinks: { labelKey: TranslationKey; to: string }[] = [
  { labelKey: "footer.nav.lerch_plus", to: "/lerch-plus" },
  { labelKey: "footer.nav.wellness", to: "/wellness" },
  { labelKey: "footer.nav.erlebnisse", to: "/erlebnisse" },
  { labelKey: "footer.nav.lage", to: "/lage-anreise" },
  { labelKey: "footer.nav.buchung", to: "/suites#buchungsinformationen" },
  { labelKey: "footer.nav.bewertungen", to: "/bewertungen" },
];

const Footer: React.FC = () => {
  const { t } = useLocale();
  return (
    <footer role="contentinfo" className="font-display mt-12 md:mt-20 lg:mt-[150px]">
      {/* Newsletter Bar */}
      <div style={{ backgroundColor: "#CCC6C8" }}>
        <Reveal className="mx-auto max-w-[1560px] px-6 lg:px-10 py-10 lg:py-[59px]">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
            <div
              className="uppercase tracking-[0.08em] font-medium shrink-0"
              style={{ color: ANTHRAZIT, fontSize: 14, letterSpacing: "1.12px" }}
            >
              <EditableT k="footer.newsletter.kicker" scope="global" />
            </div>
            <p
              className="lg:w-[424px] font-light"
              style={{ color: ANTHRAZIT, fontSize: 14, lineHeight: "20px", letterSpacing: "0.28px" }}
            >
              <EditableT k="footer.newsletter.text" scope="global" multiline />
            </p>
            <NewsletterDialog
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center justify-center h-[37px] rounded-full px-[30px] leading-none uppercase font-medium transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{
                    backgroundColor: VIOLET,
                    color: CREME,
                    fontSize: 14,
                    letterSpacing: "1.12px",
                  }}
                >
                  <EditableT k="footer.newsletter.button" scope="global" />
                </button>
              }
            />


          </div>
        </Reveal>
      </div>

      {/* Main Footer */}
      <div style={{ backgroundColor: "#A59FA1", color: CREME }}>
        <Reveal className="mx-auto max-w-[1560px] px-6 lg:px-10 pt-16 lg:pt-[140px] pb-[140px] lg:pb-[140px]">
          <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
            <div className="flex flex-row flex-wrap items-start justify-between gap-8 sm:gap-10 lg:contents">
              {/* Bildmarke */}
              <div className="shrink-0">
                <EditableImage
                  id="footer.bildmarke"
                  scope="global"
                  src={bildmarkeFooter}
                  alt={t("footer.bildmarke_alt")}
                  className="h-auto w-[110px] sm:w-[140px] lg:w-[162px]"
                />
              </div>


              {/* Kontakt – immer linksbündig */}
              <div className="flex flex-col gap-[20px] lg:gap-[28px] items-start text-left lg:self-stretch lg:justify-between lg:h-[220px]">
                <div
                  style={{ fontSize: 18, lineHeight: "30px", letterSpacing: "0.36px" }}
                  className="font-normal text-[15px] sm:text-[16px] lg:!text-[18px]"
                >
                  <div>
                    <Editable id="footer.address.line1" scope="global" defaultValue="Lerch Suites, Familie Lerch">
                      Lerch Suites, Familie Lerch
                    </Editable>
                  </div>
                  <div>
                    <Editable id="footer.address.line2" scope="global" defaultValue="Wiese 103 | 6555 Kappl">
                      Wiese 103 | 6555 Kappl
                    </Editable>
                  </div>
                </div>
                <div className="flex flex-col gap-3 items-start font-light">
                  <span
                    className="inline-flex items-center gap-[16px] lg:gap-[25px] text-[15px] sm:text-[16px] lg:!text-[18px] font-light"
                    style={{ lineHeight: "30px", letterSpacing: "0.36px" }}
                  >
                    <PhoneIcon />
                    <Editable
                      id="footer.phone"
                      type="link"
                      scope="global"
                      label="+43 664 5380244"
                      href="tel:+436645380244"
                      className="hover:opacity-80 transition-opacity"
                    />
                  </span>
                  <span
                    className="inline-flex items-center gap-[16px] lg:gap-[25px] text-[15px] sm:text-[16px] lg:!text-[18px] font-light"
                    style={{ lineHeight: "30px", letterSpacing: "0.36px" }}
                  >
                    <MailIcon />
                    <Editable
                      id="footer.email"
                      type="link"
                      scope="global"
                      label="info@lerch-suites.at"
                      href="mailto:info@lerch-suites.at"
                      className="hover:opacity-80 transition-opacity"
                    />
                  </span>
                </div>
              </div>

              {/* Sitemap Links */}
              <nav aria-label="Footer Navigation" className="flex flex-col gap-2">
                {navLinks.map((l) => (
                  <EditableTLink
                    key={l.to}
                    k={l.labelKey}
                    href={l.to}
                    scope="global"
                    className="underline underline-offset-2 hover:opacity-80 transition-opacity text-[15px] sm:text-[16px] lg:!text-[18px] text-[#F2F2F2]"
                  />
                ))}
              </nav>
            </div>

            {/* Region Logos */}
            <div className="flex flex-row items-end justify-start gap-10 sm:gap-14 lg:flex-col lg:items-center lg:gap-12 lg:w-[167px]">
              <a
                href={t("footer.region.kappl_url")}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("footer.region.kappl_aria")}
                className="hover:opacity-80 transition-opacity"
              >
                <EditableImage
                  id="footer.region.kappl_logo"
                  scope="global"
                  src={logoKappl}
                  alt="Kappl"
                  className="h-auto w-[100px] sm:w-[120px] lg:w-[132px]"
                />
              </a>
              <a
                href={t("footer.region.ischgl_url")}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("footer.region.ischgl_aria")}
                className="hover:opacity-80 transition-opacity"
              >
                <EditableImage
                  id="footer.region.ischgl_logo"
                  scope="global"
                  src={logoIschgl}
                  alt="Ischgl"
                  className="h-auto w-[120px] sm:w-[150px] lg:w-[167px]"
                />
              </a>
            </div>

          </div>

          {/* Bottom Bar */}
          <div className="mt-16 lg:mt-[120px] flex flex-row flex-wrap items-center justify-between gap-4">
            <div
              className="flex flex-nowrap items-center gap-3 sm:gap-5 lg:gap-[65px] text-[12px] sm:text-[14px] lg:text-[15px] whitespace-nowrap"
              style={{ lineHeight: "30px", letterSpacing: "0.3px" }}
            >
              <EditableTLink
                k="footer.legal_privacy"
                href="/datenschutz"
                scope="global"
                className="underline hover:opacity-80 transition-opacity text-[#F2F2F2]"
              />
              <EditableTLink
                k="footer.legal_imprint"
                href="/impressum"
                scope="global"
                className="underline hover:opacity-80 transition-opacity text-[#F2F2F2]"
              />
              <EditableTLink
                k="footer.legal_accessibility"
                href="/barrierefreiheit"
                scope="global"
                className="underline hover:opacity-80 transition-opacity text-[#F2F2F2]"
              />
              <EditableTLink
                k="footer.legal_sitemap"
                href="/sitemap"
                scope="global"
                className="underline hover:opacity-80 transition-opacity text-[#F2F2F2]"
              />
            </div>
            <div
              style={{ lineHeight: "30px", letterSpacing: "0.3px" }}
              className="text-[12px] sm:text-[14px] lg:text-[15px] lg:text-right"
            >
              Site by{" "}
              <Editable
                id="footer.site_by"
                type="link"
                scope="global"
                label="Huber Web Media"
                href={t("footer.site_by_url")}
                external
                className="underline hover:opacity-80 transition-opacity"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </footer>
  );
};

export default Footer;
